/**
 * Dashboard "Invite & earn" tab (mig 0048). Renders the user's referral link,
 * earned USDT balance, and actions to (a) transfer credit onto an eSIM or
 * (b) request a withdrawal. Works identically on web / PWA / Telegram mini-app
 * (same SPA; the tab nav is hidden in TG but the route is still reachable).
 *
 * Pattern mirrors the other dashboard panels: `referralPanel()` returns a static
 * skeleton; `wireReferralHandlers()` (called from the tab mount) fetches the
 * summary, fills the skeleton, and wires the buttons.
 */
import { escapeHtml } from '../../utils';
import { t } from '../../i18n';
import { fmtDate, fmtMoney } from '../../utils/format';
import * as api from '../../api';
import { showToast } from '../../toast';

// Amounts are "$" like the rest of the dashboard; USDT is named where it is
// the payout rail ("Paid in USDT", network, wallet) — see utils/format.ts.
// The withdrawal minimum is a payout rule, stated in the payout coin.
const usdtAmount = (micro: number): string => (micro / 1_000_000).toFixed(2);

export function referralPanel(): string {
  return `
<div class="dash-referral-panel">
  <section class="dash-referral">
    <div class="dash-referral__head">
      <h2>${escapeHtml(t('dash.referral.title'))}</h2>
      <p data-ref-subtitle data-i18n="dash.referral.subShort">${escapeHtml(t('dash.referral.subShort'))}</p>
    </div>
    <div class="dash-referral__link">
      <span class="mono" data-ref-link aria-label="${escapeHtml(t('dash.referral.yourLink'))}">—</span>
      <button class="btn btn--accent" data-ref-copy type="button" data-i18n="dash.referral.copy">${escapeHtml(t('dash.referral.copy'))}</button>
    </div>
    <div class="dash-stats-row">
      <div class="dash-stat"><span class="label" data-i18n="dash.referral.balance">${escapeHtml(t('dash.referral.balance'))}</span><span class="mono" data-ref-balance>—</span></div>
      <div class="dash-stat"><span class="label" data-i18n="dash.referral.earned">${escapeHtml(t('dash.referral.earned'))}</span><span class="mono" data-ref-earned>—</span></div>
      <div class="dash-stat"><span class="label" data-i18n="dash.referral.invited">${escapeHtml(t('dash.referral.invited'))}</span><span class="mono" data-ref-invited>—</span></div>
    </div>
  </section>

  <section class="dash-settings__group" data-ref-actions hidden>
    <h3>${escapeHtml(t('dash.referral.useBalance'))}</h3>

    <div class="dash-referral__form dash-referral__form--transfer">
      <label class="dash-referral__field"><span data-i18n="dash.referral.toEsim">${escapeHtml(t('dash.referral.toEsim'))}</span>
        <select class="dash-referral__input" data-ref-esim></select></label>
      <label class="dash-referral__field"><span data-i18n="dash.referral.amountLabel">${escapeHtml(t('dash.referral.amountLabel'))}</span>
        <input class="dash-referral__input" type="number" min="1" step="1" inputmode="decimal" data-ref-transfer-amount /></label>
      <button class="btn btn--outline" data-ref-transfer type="button" data-i18n="dash.referral.transferBtn">${escapeHtml(t('dash.referral.transferBtn'))}</button>
    </div>

    <div class="dash-referral__form dash-referral__form--withdraw">
      <label class="dash-referral__field"><span data-i18n="dash.referral.amountLabel">${escapeHtml(t('dash.referral.amountLabel'))}</span>
        <input class="dash-referral__input" type="number" min="50" step="1" inputmode="decimal" data-ref-wd-amount /></label>
      <label class="dash-referral__field"><span data-i18n="dash.referral.network">${escapeHtml(t('dash.referral.network'))}</span>
        <select class="dash-referral__input" data-ref-network></select></label>
      <label class="dash-referral__field"><span data-i18n="dash.referral.address">${escapeHtml(t('dash.referral.address'))}</span>
        <input class="dash-referral__input" data-ref-address placeholder="0x… / T…" /></label>
      <button class="btn btn--outline" data-ref-withdraw type="button" data-i18n="dash.referral.withdrawBtn">${escapeHtml(t('dash.referral.withdrawBtn'))}</button>
    </div>

    <p class="dash-referral__note" data-ref-minnote></p>
    <p class="dash-referral__note dash-referral__note--warn" data-ref-claimnote hidden data-i18n="dash.referral.claimFirst">${escapeHtml(t('dash.referral.claimFirst'))}</p>
  </section>

  <section class="dash-settings__group">
    <h3>${escapeHtml(t('dash.referral.activity'))}</h3>
    <div data-ref-history><p class="dash-referral__empty" data-i18n="dash.referral.loading">${escapeHtml(t('dash.referral.loading'))}</p></div>
  </section>
</div>`;
}

function statusLabel(status: string): string {
  switch (status) {
    case 'pending': return t('dash.referral.wd.pending');
    case 'approved': return t('dash.referral.wd.approved');
    case 'paid': return t('dash.referral.wd.paid');
    case 'rejected': return t('dash.referral.wd.rejected');
    case 'cancelled': return t('dash.referral.wd.cancelled');
    default: return status;
  }
}

function renderHistory(el: HTMLElement, s: api.ReferralSummaryResp): void {
  const rows: string[] = [];
  for (const w of s.withdrawals) {
    rows.push(`<div class="dash-referral__activity-row"><span class="amt">${fmtMoney(-Math.abs(w.amountMicroUsdt))} · ${escapeHtml(w.network)}</span><span class="meta">${escapeHtml(statusLabel(w.status))} · ${escapeHtml(fmtDate(w.createdAt))}</span></div>`);
  }
  for (const r of s.recent) {
    const incoming = r.amountMicroUsdt >= 0;
    const label = r.type === 'earn' ? t('dash.referral.earnRow')
      : r.type === 'transfer-to-esim' ? t('dash.referral.transferRow')
      : r.type === 'withdraw' ? t('dash.referral.withdrawRow')
      : r.type === 'withdraw-refund' ? t('dash.referral.refundRow')
      : r.type;
    rows.push(`<div class="dash-referral__activity-row${r.voided ? ' is-voided' : ''}"><span class="amt${incoming ? ' amt--in' : ''}">${fmtMoney(r.amountMicroUsdt, { signed: true })}</span><span class="meta">${escapeHtml(label)} · ${escapeHtml(fmtDate(r.createdAt))}</span></div>`);
  }
  el.innerHTML = rows.length ? rows.join('') : `<p class="dash-referral__empty" data-i18n="dash.referral.empty">${escapeHtml(t('dash.referral.empty'))}</p>`;
}

export async function wireReferralHandlers(): Promise<void> {
  // Scope to the OUTER panel wrapper — the actions + activity sections are
  // siblings of the `.dash-referral` hero card, not inside it (redesign 2026-05-31).
  const root = document.querySelector<HTMLElement>('.dash-referral-panel');
  if (!root) return;

  const $ = <T extends HTMLElement>(sel: string): T | null => root.querySelector<T>(sel);

  let summary: api.ReferralSummaryResp | null = null;

  const fill = (s: api.ReferralSummaryResp): void => {
    summary = s;
    const linkEl = $('[data-ref-link]');
    if (linkEl) linkEl.textContent = s.link;
    const sub = $('[data-ref-subtitle]');
    if (sub) sub.textContent = t('dash.referral.sub', { percent: String(s.percent) });
    const bal = $('[data-ref-balance]'); if (bal) bal.textContent = fmtMoney(s.balanceMicroUsdt);
    const earned = $('[data-ref-earned]'); if (earned) earned.textContent = fmtMoney(s.earnedTotalMicroUsdt);
    const inv = $('[data-ref-invited]'); if (inv) inv.textContent = String(s.invitedCount);

    const minNote = $('[data-ref-minnote]');
    if (minNote) minNote.textContent = t('dash.referral.minNote', { min: usdtAmount(s.minWithdrawalMicroUsdt) });

    // Network select
    const netSel = $<HTMLSelectElement>('[data-ref-network]');
    if (netSel && !netSel.options.length) {
      netSel.innerHTML = s.networks.map((n) => `<option value="${escapeHtml(n)}">${escapeHtml(n)}</option>`).join('');
    }

    // Claim gate on withdrawals
    const claimNote = $('[data-ref-claimnote]');
    const wdBtn = $<HTMLButtonElement>('[data-ref-withdraw]');
    if (!s.canWithdraw) {
      if (claimNote) claimNote.hidden = false;
      if (wdBtn) wdBtn.disabled = true;
    }

    const actions = $('[data-ref-actions]') as HTMLElement | null;
    if (actions) actions.hidden = false;

    const hist = $('[data-ref-history]');
    if (hist) renderHistory(hist, s);
  };

  const reload = async (): Promise<void> => {
    try {
      const s = await api.fetchReferral();
      fill(s);
    } catch {
      const hist = $('[data-ref-history]');
      if (hist) hist.innerHTML = `<p class="dash-sub" data-i18n="dash.referral.loadError">${escapeHtml(t('dash.referral.loadError'))}</p>`;
    }
  };

  // Populate eSIM select (active eSIMs only).
  void (async () => {
    try {
      const list = await api.fetchEsims();
      const sel = $<HTMLSelectElement>('[data-ref-esim]');
      if (!sel) return;
      const usable = list.esims.filter((e) => e.status === 'active' || e.status === 'paused');
      sel.innerHTML = usable.map((e) => {
        const id = e.msisdn ?? e.displayId ?? e.id.slice(-8);
        return `<option value="${escapeHtml(e.id)}">${escapeHtml(id)} · ${fmtMoney(e.balanceMicroUsdt)}</option>`;
      }).join('') || `<option value="">—</option>`;
    } catch { /* leave empty */ }
  })();

  await reload();

  // Copy link
  $('[data-ref-copy]')?.addEventListener('click', () => {
    const link = summary?.link;
    if (!link) return;
    void navigator.clipboard?.writeText(link);
    showToast(t('dash.referral.copied'), 'success');
  });

  // Transfer to eSIM
  $('[data-ref-transfer]')?.addEventListener('click', () => {
    void (async () => {
      const esimId = $<HTMLSelectElement>('[data-ref-esim]')?.value;
      const amtUsdt = parseFloat($<HTMLInputElement>('[data-ref-transfer-amount]')?.value ?? '');
      if (!esimId || !Number.isFinite(amtUsdt) || amtUsdt <= 0) {
        showToast(t('dash.referral.invalidAmount'), 'error');
        return;
      }
      const micro = Math.round(amtUsdt * 1_000_000);
      try {
        await api.transferReferralToEsim(esimId, micro);
        showToast(t('dash.referral.transferOk'), 'success');
        await reload();
      } catch (err) {
        showToast(`${t('dash.referral.actionFailed')}: ${(err as api.ApiError).body && (err as api.ApiError).status === 400 ? ((err as api.ApiError).body as { error?: string }).error ?? '' : (err as Error).message}`, 'error');
      }
    })();
  });

  // Withdraw
  $('[data-ref-withdraw]')?.addEventListener('click', () => {
    void (async () => {
      const amtUsdt = parseFloat($<HTMLInputElement>('[data-ref-wd-amount]')?.value ?? '');
      const network = $<HTMLSelectElement>('[data-ref-network]')?.value ?? '';
      const address = ($<HTMLInputElement>('[data-ref-address]')?.value ?? '').trim();
      if (!Number.isFinite(amtUsdt) || amtUsdt <= 0) { showToast(t('dash.referral.invalidAmount'), 'error'); return; }
      if (summary && amtUsdt * 1_000_000 < summary.minWithdrawalMicroUsdt) {
        showToast(t('dash.referral.belowMin', { min: usdtAmount(summary.minWithdrawalMicroUsdt) }), 'error');
        return;
      }
      if (!address) { showToast(t('dash.referral.needAddress'), 'error'); return; }
      const micro = Math.round(amtUsdt * 1_000_000);
      try {
        await api.requestReferralWithdrawal(micro, address, network);
        showToast(t('dash.referral.withdrawOk'), 'success');
        await reload();
      } catch (err) {
        const e = err as api.ApiError;
        const reason = e.status === 400 && e.body ? (e.body as { error?: string }).error ?? '' : (err as Error).message;
        showToast(`${t('dash.referral.actionFailed')}: ${reason}`, 'error');
      }
    })();
  });
}
